import React, {useEffect, useState, useRef, useMemo} from 'react';
import {
  Box as MuiBox,
  Button,
  Tabs,
  Tab,
  Typography,
  Divider,
  Badge
} from '@mui/material';
import {Box} from '@mui/system';
import RefreshIcon from "@mui/icons-material/Refresh";
import moment from 'moment-timezone';
import {useQuery} from '@apollo/client';
import {Outlet} from 'react-router-dom';

import {useTimezone} from '../hooks/useTimezone';
import {RefreshProvider} from '../contexts/RefreshContext';

import LoadingProgressBar from '../components/common/LoadingProgressBar';
import TimeframeSelector from '../components/LiveDashboard/TimeframeSelector';
import HealthFilterSelector from '../components/LiveDashboard/HealthFilterSelector';
import SessionsTable from '../components/LiveDashboard/SessionsTable';
import {DEFAULT_SESSION_HEALTH_FILTER, SESSION_SCOPE} from '../helpers/SessionHelper';
import {FETCH_SESSION_ISSUE_COUNT, FETCH_SESSIONS} from '../queries/GraphQLQueries';

const FULL_PAGE_REFRESH_INTERVAL_MILLISECONDS = 1000;

const COLORS = {
  disabledTab: '#999999',
  liveTab: '#000000',
};

const NO_SESSIONS_MESSAGES = {
  upcoming: 'No upcoming sessions',
  live: 'No current live sessions',
  issues: 'No sessions with issues',
};

const SESSION_HEALTH_FILTER_ISSUES_ONLY = 'ONLY_SESSIONS_WITH_ISSUES';

const PAGE_SIZE = 20;
const FIRST_PAGE_INDEX = 0;

const styles = {
  box: {
    container: {
      display: 'flex',
      flexDirection: 'column',
      width: '100%',
      backgroundColor: '#FFFFFF',
      paddingTop: '0rem',
      padding: '1rem',
      '& > *': {
        width: '100%',
        backgroundColor: '#FFFFFF',
      },
    },
    tabsContainer: {
      display: 'flex',
      alignItems: 'center',
      marginTop: '-1.25rem',
    },
    filter: {
      display: 'flex',
      alignItems: 'center',
      gap: '1rem',
      margin: '0.5rem',
      marginLeft: 0,
    },
    progress: {
      width: '100%',
      height: '0.25rem',
    },
    badgeLabel: {
      display: 'flex',
      alignItems: 'center',
    },
  },
  tabs: {
    root: {
      margin: 0,
      padding: 0,
      marginLeft: '0rem',
      '& .MuiTab-root': {
        minWidth: 'auto',
        '& .MuiTab-wrapper': {
          display: 'inline-flex',
          alignItems: 'center',
        },
      },
    },
    sx: {
      '& .MuiTabs-flexContainer': {
        gap: '0rem',
      },
    },
  },
  tab: {
    textTransform: 'none',
    color: COLORS.disabledTab,
    fontSize: '1rem',
    '&.Mui-selected': {
      color: COLORS.liveTab,
    },
  },
  divider: {
    first: {
      height: '1rem',
      backgroundColor: COLORS.liveTab,
      marginTop: '1rem',
      marginRight: '-0.5rem',
      marginLeft: '-1rem',
    },
    second: {
      height: '1rem',
      backgroundColor: COLORS.liveTab,
      marginTop: '1rem',
      marginRight: '-0.5rem',
      marginLeft: '-0.5rem',
    },
  },
  button: {
    refresh: {
      minWidth: 'auto',
      width: '2.3rem',
    },
  },
  badge: {
    issueCount: {
      '& .MuiBadge-badge': {
        right: -9,
        fontSize: '0.75rem',
        padding: '0 0.25rem',
      },
    },
  },
  table: {
    width: '100%',
    marginLeft: '0.5rem',
    marginTop: '1rem',
    backgroundColor: '#FFFFFF',
  },
  typography: {
    lastRefresh: {
      variant: 'caption',
      color: '#757575',
      marginBottom: '0.5rem',
      padding: 0,
      fontSize: '0.625rem',
    },
  },
};

const getBadgeLabelBoxStyles = (issueCount) => ({
  ...styles.box.badgeLabel,
  pr: issueCount > 0 ? '1rem' : 0,
});

function LastRefreshTimeDisplay({ timezone, lastRefreshed }) {
  const [currentTime, setCurrentTime] = useState(moment.tz(timezone));

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTime(moment.tz(timezone));
    }, FULL_PAGE_REFRESH_INTERVAL_MILLISECONDS);
    return () => clearInterval(interval);
  }, [timezone]);

  const calculateLastRefreshedTime = () => {
    const diffSeconds = currentTime.diff(lastRefreshed, 'seconds');
    return { __html: `Last Refreshed ${diffSeconds} seconds ago` };
  };

  return (
    <Typography style={styles.typography.lastRefresh} dangerouslySetInnerHTML={calculateLastRefreshedTime()} />
  );
}

export default function LiveDashboard() {
  const { timezone } = useTimezone();
  const [scope, setScope] = useState(SESSION_SCOPE.LIVE);
  const [lastRefreshed, setLastRefreshed] = useState(moment.tz(timezone));
  const [queryTime, setQueryTime] = useState(moment.tz(timezone));
  const [showProgress, setShowProgress] = useState(false);
  const [hoursTimeframe, setHoursTimeframe] = useState(1);
  const [healthFilter, setHealthFilter] = useState(DEFAULT_SESSION_HEALTH_FILTER);
  const [issuesCounts, setIssuesCounts] = useState({ live: 0, upcoming: 0 });

  const DATA_REFRESH_INTERVAL_IN_MILLISECOND = 15000;

  const [paginationModel, setPaginationModel] = useState({page: FIRST_PAGE_INDEX, pageSize: PAGE_SIZE});

  const intervalRef = useRef(null);

  const getQueryTimes = (currentScope = scope, currentQueryTime = queryTime) => {
    const now = moment.tz(currentQueryTime, timezone);
    let from = now;
    let to = now;

    if (currentScope === SESSION_SCOPE.UPCOMING) {
      from = now.clone();
      to = now.clone().add(hoursTimeframe, 'hours');
    } else if (currentScope === SESSION_SCOPE.PAST) {
      from = now.clone().subtract(hoursTimeframe, 'hours');
      to = now.clone();
    }

    return { from: from.toISOString(true), to: to.toISOString(true) };
  };

  const { from, to } = getQueryTimes();

  const { loading, error, data, previousData, refetch } = useQuery(FETCH_SESSIONS, {
    variables: {
      from,
      to,
      sessionTypes: ['TEACHING'],
      pageNumber: paginationModel.page,
      pageSize: paginationModel.pageSize,
      sessionScope : scope,
      healthFilter: healthFilter
    },
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
  });

  const { data: liveIssuesData, loading: liveIssuesLoading } = useQuery(FETCH_SESSION_ISSUE_COUNT, {
    variables: {
      from,
      to,
      pageNumber: paginationModel.page,
      pageSize: paginationModel.pageSize,
      sessionTypes: ['TEACHING'],
      sessionScope: SESSION_SCOPE.LIVE,
      healthFilter: SESSION_HEALTH_FILTER_ISSUES_ONLY,
    },
    fetchPolicy: 'network-only',
  });

  const { data: upcomingIssuesData, loading: upcomingIssuesLoading } = useQuery(FETCH_SESSION_ISSUE_COUNT, {
    variables: {
      from: moment.tz(queryTime, timezone).clone(),
      to: moment.tz(queryTime, timezone).clone().add(hoursTimeframe, 'hours'),
      pageNumber: paginationModel.page,
      pageSize: paginationModel.pageSize,
      sessionTypes: ['TEACHING'],
      sessionScope: SESSION_SCOPE.UPCOMING,
      healthFilter: SESSION_HEALTH_FILTER_ISSUES_ONLY,
    },
    fetchPolicy: 'network-only',
  });

  const filteredSessionRowCountRef = useRef(data?.fetchSessions.totalSessionsCount || 0);

  const handleTabChange = (event, newValue) => {
    setScope(newValue);
    setHealthFilter(DEFAULT_SESSION_HEALTH_FILTER);

    const timeframe = moment.tz(timezone);
    setQueryTime(timeframe);
    setLastRefreshed(timeframe);

    const { from, to } = getQueryTimes(newValue, timeframe);
    refetch({ from, to });
  };

  const handleRefresh = () => {
    const timeframe = moment.tz(timezone);
    setLastRefreshed(timeframe);
    setQueryTime(timeframe);

    const { from, to } = getQueryTimes(scope, timeframe);
    refetch({ from, to });
  };

  const handlePaginationChange = (newModel) => {
    const timeframe = moment.tz(timezone);
    setLastRefreshed(timeframe);
    setQueryTime(timeframe);

    const { from, to } = getQueryTimes(scope, timeframe);
    setPaginationModel(newModel);
    refetch({ from, to });
  };

  const handleTimeframeChange = (selectedHours) => {
    setHoursTimeframe(selectedHours);

    const timeframe = moment.tz(timezone);
    setQueryTime(timeframe);
    setLastRefreshed(timeframe);

    const { from, to } = getQueryTimes(scope, timeframe);
    refetch({ from, to });
  };

  const handleHealthFilterChange = (value) => {
    setHealthFilter(value);
  };

  const sessions = data?.fetchSessions?.sessions || previousData?.fetchSessions?.sessions || [];

  useEffect(() => {
    if (!liveIssuesLoading && !upcomingIssuesLoading) {
      setIssuesCounts({
        live: liveIssuesData?.fetchSessions?.totalSessionsCount || 0,
        upcoming: upcomingIssuesData?.fetchSessions?.totalSessionsCount || 0
      });
    }
  }, [liveIssuesData, upcomingIssuesData, liveIssuesLoading, upcomingIssuesLoading]);

  const handleProgressComplete = () => {
    setShowProgress(false);
  };

  useEffect(() => {
    const startInterval = () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
      intervalRef.current = setInterval(() => {
        const newQueryTime = moment.tz(timezone);
        setQueryTime(newQueryTime);
        setLastRefreshed(newQueryTime);
        const { from, to } = getQueryTimes(scope, newQueryTime);
        refetch({ from, to });
      }, DATA_REFRESH_INTERVAL_IN_MILLISECOND);
    };

    startInterval();

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [timezone, refetch, scope]);

  useEffect(() => {
    if (loading) {
      setShowProgress(true);
    }
  }, [loading]);

  const rowCount = useMemo(() => {
    if (data?.fetchSessions.totalSessionsCount !== undefined) {
      filteredSessionRowCountRef.current = data?.fetchSessions.totalSessionsCount;
    }

    return filteredSessionRowCountRef.current;
  }, [data?.fetchSessions.totalSessionsCount]);

  if (error) return <p>Error: {error.message}</p>;

  const noRowsMessage =
  scope === SESSION_SCOPE.UPCOMING && healthFilter === SESSION_HEALTH_FILTER_ISSUES_ONLY
      ? NO_SESSIONS_MESSAGES.issues
      : NO_SESSIONS_MESSAGES[scope];

  return (
    <RefreshProvider trigger={lastRefreshed}>
      <MuiBox style={styles.box.container}>
        <MuiBox style={styles.box.tabsContainer}>
          <Button onClick={handleRefresh} variant="contained" size="small" sx={styles.button.refresh}>
            <RefreshIcon />
          </Button>
          <Tabs
            value={scope}
            onChange={handleTabChange}
            TabIndicatorProps={{style: {display: 'none'}}}
            style={styles.tabs.root}
            sx={styles.tabs.sx}
          >
            <Tab label="Past" value={SESSION_SCOPE.PAST} sx={styles.tab} />
            <Divider orientation="vertical" flexItem sx={styles.divider.first} />
            <Tab
              label={
                <Box sx={getBadgeLabelBoxStyles(issuesCounts.live)}>
                  <Badge badgeContent={issuesCounts.live} color="error" sx={styles.badge.issueCount}>
                    Live Sessions
                  </Badge>
                </Box>
              }
              value={SESSION_SCOPE.LIVE}
              sx={styles.tab}
            />
            <Divider orientation="vertical" flexItem sx={styles.divider.second} />
            <Tab
              label={
                <Box sx={getBadgeLabelBoxStyles(issuesCounts.upcoming)}>
                  <Badge badgeContent={issuesCounts.upcoming} color="error" sx={styles.badge.issueCount}>
                    Upcoming
                  </Badge>
                </Box>
              }
              value={SESSION_SCOPE.UPCOMING}
              sx={styles.tab}
            />
          </Tabs>
        </MuiBox>

        {
          scope !== SESSION_SCOPE.LIVE
          &&
          <MuiBox style={styles.box.filter}>
            <TimeframeSelector
              hoursTimeframe={hoursTimeframe}
              onTimeframeChange={handleTimeframeChange}
              scope={scope}
            />
            <HealthFilterSelector
              healthFilter={healthFilter}
              onHealthFilterChange={handleHealthFilterChange}
            />
          </MuiBox>
        }

        <LastRefreshTimeDisplay timezone={timezone} lastRefreshed={lastRefreshed} />

        <MuiBox sx={styles.box.progress}>
          {showProgress && <LoadingProgressBar onComplete={handleProgressComplete} />}
        </MuiBox>

        <SessionsTable
          refetchSessionsData={refetch}
          style={styles.table}
          sessions={sessions}
          noRowsMessage={noRowsMessage}
          paginationModel={paginationModel}
          onPaginationModelChange={handlePaginationChange}
          rowCount={rowCount}
        />
      </MuiBox>
      <Outlet />
    </RefreshProvider>
  );
}